import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Clock, Home, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  
  const paymentId = searchParams.get('payment_id');
  const sumupStatus = searchParams.get('status'); // SumUp passes status in URL
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!paymentId || !condoId || !token) {
        setStatus('error');
        return;
      }
      
      try {
        const res = await fetch(
          `${API_URL}/api/condominiums/${condoId}/payments/${paymentId}/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (res.ok) {
          const data = await res.json();
          setPaymentInfo(data);
          
          // Check status from API or from URL param
          if (data.status === 'PAID' || sumupStatus === 'success') {
            setStatus('success');
            toast.success('Pagamento completato con successo!');
          } else if (data.status === 'FAILED' || sumupStatus === 'failed') {
            setStatus('failed');
            toast.error('Pagamento non riuscito');
          } else if (data.status === 'CANCELLED' || sumupStatus === 'cancelled') {
            setStatus('cancelled');
          } else {
            setStatus('pending');
          }
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };
    
    checkPaymentStatus();
  }, [paymentId, condoId, token, sumupStatus]);
  
  const getStatusDisplay = () => {
    switch (status) {
      case 'success':
        return {
          icon: <CheckCircle2 className="h-16 w-16 text-green-500" />,
          title: 'Pagamento Completato!',
          message: 'Il tuo pagamento è stato elaborato con successo. La tua quota è ora saldata.',
          color: 'bg-green-50 border-green-200'
        };
      case 'failed':
        return {
          icon: <XCircle className="h-16 w-16 text-red-500" />,
          title: 'Pagamento Non Riuscito',
          message: 'Si è verificato un errore durante il pagamento. Ti preghiamo di riprovare.',
          color: 'bg-red-50 border-red-200'
        };
      case 'cancelled':
        return {
          icon: <XCircle className="h-16 w-16 text-orange-500" />,
          title: 'Pagamento Annullato',
          message: 'Hai annullato il pagamento. Puoi riprovare quando vuoi.',
          color: 'bg-orange-50 border-orange-200'
        };
      case 'pending':
        return {
          icon: <Clock className="h-16 w-16 text-yellow-500 animate-pulse" />,
          title: 'Pagamento in Elaborazione',
          message: 'Il tuo pagamento è in fase di elaborazione. Riceverai conferma a breve.',
          color: 'bg-yellow-50 border-yellow-200'
        };
      case 'loading':
        return {
          icon: <Clock className="h-16 w-16 text-blue-500 animate-spin" />,
          title: 'Verifica in corso...',
          message: 'Stiamo verificando lo stato del tuo pagamento.',
          color: 'bg-blue-50 border-blue-200'
        };
      default:
        return {
          icon: <XCircle className="h-16 w-16 text-gray-500" />,
          title: 'Errore',
          message: 'Non è stato possibile verificare lo stato del pagamento.',
          color: 'bg-gray-50 border-gray-200'
        };
    }
  };
  
  const statusDisplay = getStatusDisplay();
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-6 p-4">
      <div className="w-full max-w-md">
        <PageHeader title="Esito Pagamento" subtitle="Riepilogo della transazione" />
      </div>
      <Card className={`max-w-md w-full ${statusDisplay.color}`}>
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {statusDisplay.icon}
          </div>
          <CardTitle className="text-2xl">{statusDisplay.title}</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          <p className="text-muted-foreground">
            {statusDisplay.message}
          </p>
          
          {paymentInfo && (
            <div className="bg-white rounded-lg p-4 text-left">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">Importo:</span>
                <span className="font-semibold">€ {paymentInfo.amount?.toFixed(2)}</span>
                <span className="text-muted-foreground">Metodo:</span>
                <span className="font-semibold">
                  {paymentInfo.payment_method === 'card' ? 'Carta di credito' : 'Bonifico'}
                </span>
                {paymentInfo.paid_at && (
                  <>
                    <span className="text-muted-foreground">Data:</span>
                    <span className="font-semibold">
                      {new Date(paymentInfo.paid_at).toLocaleDateString('it-IT')}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
          
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate('/monthly-quotes')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna alle Quote
            </Button>
            <Button onClick={() => navigate('/dashboard')}>
              <Home className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentResult;
